/**
 * Built-in `image` widget — uploads one or more images (or PDFs) and shows
 * them with a prev/next pager.
 *
 * Storage model: each file is base64-encoded and stored as a resource on the
 * layout (`ctx.addResource({ type: 'image', mimeType, name, data })`). The
 * widget's `config.images` is just an array of `{ $ref }` placeholders — the
 * actual blobs live in `layout.resources` at the bottom of the layout JSON
 * (glTF-style). On layout save the dashboard GCs any resources no cell still
 * references.
 *
 * `currentIndex` is a transient view choice (which page the user is looking
 * at) so it lives in `state`, not config.
 */
import type {
  DashboardWidget,
  DashboardWidgetInstance,
  DashboardCtx,
  ResourceRef,
} from '../../Widget';
import { isResourceRef } from '../../Widget';

export interface ImageWidgetConfig {
  images: ResourceRef[];
  /** Auto-advance to the next image every N seconds. 0 / undefined = off. */
  autoAdvanceSeconds?: number;
}

interface ImageWidgetState {
  currentIndex?: number;
}

const ACCEPT =
  'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,application/pdf,.png,.jpg,.jpeg,.gif,.webp,.svg,.pdf';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      // FileReader returns "data:<mime>;base64,<payload>" — strip the prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = () => reject(new Error('file read failed'));
    r.readAsDataURL(file);
  });
}

function inferMime(file: File): string {
  if (file.type) return file.type;
  const n = file.name.toLowerCase();
  if (n.endsWith('.pdf')) return 'application/pdf';
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.gif')) return 'image/gif';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.svg')) return 'image/svg+xml';
  return 'application/octet-stream';
}

class ImageWidgetInstance implements DashboardWidgetInstance {
  private host: HTMLElement;
  private config: ImageWidgetConfig;
  private ctx: DashboardCtx;
  private currentIndex = 0;
  private bodyEl: HTMLElement | null = null;
  private pagerEl: HTMLElement | null = null;
  private captionEl: HTMLElement | null = null;
  private mediaEl: HTMLElement | null = null;
  private autoTimer: ReturnType<typeof setInterval> | null = null;

  constructor(host: HTMLElement, config: ImageWidgetConfig, ctx: DashboardCtx) {
    this.host = host;
    this.config = config;
    this.ctx = ctx;
    this.render();
    this.startAutoAdvance();
  }

  private stopAutoAdvance(): void {
    if (this.autoTimer != null) {
      clearInterval(this.autoTimer);
      this.autoTimer = null;
    }
  }

  private startAutoAdvance(): void {
    this.stopAutoAdvance();
    const secs = this.config.autoAdvanceSeconds;
    if (typeof secs !== 'number' || !Number.isFinite(secs) || secs <= 0) return;
    if (!this.config.images || this.config.images.length < 2) return;
    this.autoTimer = setInterval(() => {
      // Advance with looping; gotoPage already wraps at the ends.
      this.gotoPage(this.currentIndex + 1);
    }, Math.max(0.5, secs) * 1000);
  }

  private render(): void {
    this.host.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'position:absolute;inset:0;display:flex;flex-direction:column;background:#0c0f13;color:#d8dee9';
    this.host.appendChild(wrap);
    this.bodyEl = wrap;

    if (!this.config.images || this.config.images.length === 0) {
      const ph = document.createElement('div');
      ph.style.cssText =
        'flex:1 1 auto;display:flex;align-items:center;justify-content:center;color:#7d8a96;font-size:12px;text-align:center;padding:8px;line-height:1.4';
      ph.textContent = 'No images — open Configure to upload (PNG / JPG / GIF / WebP / SVG / PDF).';
      wrap.appendChild(ph);
      return;
    }

    // Clamp index to range.
    if (this.currentIndex < 0 || this.currentIndex >= this.config.images.length) {
      this.currentIndex = 0;
    }

    const media = document.createElement('div');
    media.style.cssText =
      'flex:1 1 auto;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#06080b';
    wrap.appendChild(media);
    this.mediaEl = media;

    const footer = document.createElement('div');
    footer.style.cssText =
      'flex:0 0 auto;display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 8px;background:#0f1318;border-top:1px solid #1c222a;font-size:11px;color:#9aa4ad';
    wrap.appendChild(footer);

    const caption = document.createElement('div');
    caption.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1 1 auto';
    footer.appendChild(caption);
    this.captionEl = caption;

    const pager = document.createElement('div');
    pager.style.cssText = 'display:flex;align-items:center;gap:4px;flex:0 0 auto';
    const btnStyle =
      'background:#14181d;color:#d8dee9;border:1px solid #2a313a;border-radius:3px;padding:2px 8px;cursor:pointer;font-size:11px';

    const prev = document.createElement('button');
    prev.textContent = '‹';
    prev.style.cssText = btnStyle;
    prev.addEventListener('click', () => this.gotoPage(this.currentIndex - 1));

    const counter = document.createElement('div');
    counter.style.cssText = 'min-width:48px;text-align:center';

    const next = document.createElement('button');
    next.textContent = '›';
    next.style.cssText = btnStyle;
    next.addEventListener('click', () => this.gotoPage(this.currentIndex + 1));

    pager.appendChild(prev);
    pager.appendChild(counter);
    pager.appendChild(next);
    footer.appendChild(pager);
    this.pagerEl = counter;

    this.renderCurrentImage();
  }

  private renderCurrentImage(): void {
    if (!this.mediaEl || !this.pagerEl || !this.captionEl) return;
    this.mediaEl.innerHTML = '';
    const total = this.config.images?.length ?? 0;
    if (total === 0) {
      this.pagerEl.textContent = '0 / 0';
      this.captionEl.textContent = '';
      return;
    }
    this.pagerEl.textContent = `${this.currentIndex + 1} / ${total}`;

    const ref = this.config.images[this.currentIndex];
    const res = this.ctx.getResource(ref);
    if (!res || !res.data) {
      const err = document.createElement('div');
      err.style.cssText = 'color:#f0a4a4;font-size:12px;text-align:center;padding:8px';
      err.textContent = `Missing resource: ${typeof ref === 'object' ? ref.$ref : String(ref)}`;
      this.mediaEl.appendChild(err);
      this.captionEl.textContent = '';
      return;
    }
    this.captionEl.textContent = res.name ?? '';
    const url = `data:${res.mimeType ?? 'application/octet-stream'};base64,${res.data}`;
    if (res.mimeType === 'application/pdf') {
      const iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.style.cssText = 'width:100%;height:100%;border:0;background:#fff';
      this.mediaEl.appendChild(iframe);
    } else {
      const img = document.createElement('img');
      img.src = url;
      img.alt = res.name ?? '';
      img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;display:block';
      this.mediaEl.appendChild(img);
    }
  }

  private gotoPage(idx: number): void {
    const total = this.config.images?.length ?? 0;
    if (total === 0) return;
    if (idx < 0) idx = total - 1;
    if (idx >= total) idx = 0;
    this.currentIndex = idx;
    this.renderCurrentImage();
  }

  renderConfigUI(host: HTMLElement, onChange: (cfg: unknown) => void): void {
    host.innerHTML = '';
    const form = document.createElement('div');
    form.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:12px;min-width:280px';

    const info = document.createElement('div');
    info.style.cssText = 'color:#9aa4ad;line-height:1.4';
    info.textContent =
      'Upload images (PNG / JPG / GIF / WebP / SVG) or PDFs. Each file is embedded as base64 in the layout JSON under `resources`; the cell references it by id.';
    form.appendChild(info);

    const status = document.createElement('div');
    status.style.cssText = 'font-size:11px;min-height:14px;color:#7d8a96';
    const setStatus = (text: string, danger = false): void => {
      status.textContent = text;
      status.style.color = danger ? '#f0a4a4' : '#7d8a96';
    };

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap';

    const fileBtn = document.createElement('button');
    fileBtn.type = 'button';
    fileBtn.textContent = 'Upload images…';
    fileBtn.style.cssText =
      'padding:4px 10px;font-size:11px;background:#2c5cff;color:#fff;border:0;border-radius:3px;cursor:pointer';

    const fileIn = document.createElement('input');
    fileIn.type = 'file';
    fileIn.multiple = true;
    fileIn.accept = ACCEPT;
    fileIn.style.display = 'none';

    fileBtn.addEventListener('click', () => fileIn.click());
    fileIn.addEventListener('change', async () => {
      const files = Array.from(fileIn.files ?? []);
      if (files.length === 0) return;
      setStatus(`Reading ${files.length} file(s)…`);
      try {
        const added: ResourceRef[] = [];
        for (const f of files) {
          const data = await fileToBase64(f);
          const ref = this.ctx.addResource({
            type: 'image',
            name: f.name,
            mimeType: inferMime(f),
            data,
          });
          added.push(ref);
        }
        const nextCfg: ImageWidgetConfig = {
          images: [...(this.config.images ?? []), ...added],
          autoAdvanceSeconds: this.config.autoAdvanceSeconds,
        };
        setStatus(`Added ${added.length} image(s).`);
        onChange(nextCfg);
      } catch (e) {
        setStatus(`Upload failed: ${(e as Error).message}`, true);
      } finally {
        fileIn.value = '';
      }
    });

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.textContent = 'Remove all';
    clearBtn.style.cssText =
      'padding:4px 10px;font-size:11px;background:#14181d;color:#d8dee9;border:1px solid #2a313a;border-radius:3px;cursor:pointer';
    clearBtn.addEventListener('click', () => {
      onChange({
        images: [],
        autoAdvanceSeconds: this.config.autoAdvanceSeconds,
      } satisfies ImageWidgetConfig);
    });

    row.appendChild(fileBtn);
    row.appendChild(fileIn);
    row.appendChild(clearBtn);
    form.appendChild(row);

    // Auto-advance seconds — apply on change. Loops automatically (the pager
    // wraps at end-of-list).
    const advRow = document.createElement('label');
    advRow.style.cssText = 'display:flex;align-items:center;gap:6px;font-size:11px;color:#9aa4ad';
    const advLabel = document.createElement('span');
    advLabel.textContent = 'Auto-advance every';
    const advIn = document.createElement('input');
    advIn.type = 'number';
    advIn.min = '0';
    advIn.step = '0.5';
    advIn.style.cssText =
      'width:64px;padding:2px 6px;font-size:11px;background:#0c0f13;color:#d8dee9;border:1px solid #2a313a;border-radius:3px';
    advIn.value = String(this.config.autoAdvanceSeconds ?? 0);
    const advUnit = document.createElement('span');
    advUnit.textContent = 'seconds  (0 = off, loops)';
    advRow.appendChild(advLabel);
    advRow.appendChild(advIn);
    advRow.appendChild(advUnit);
    form.appendChild(advRow);

    const emitAdv = (): void => {
      const n = Number(advIn.value);
      const secs = Number.isFinite(n) && n > 0 ? n : 0;
      onChange({
        images: this.config.images ?? [],
        autoAdvanceSeconds: secs > 0 ? secs : undefined,
      } satisfies ImageWidgetConfig);
    };
    advIn.addEventListener('change', emitAdv);

    // List currently-attached images.
    const list = document.createElement('div');
    list.style.cssText = 'display:flex;flex-direction:column;gap:4px;margin-top:4px';
    const images = this.config.images ?? [];
    if (images.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'color:#5f6a73;font-size:11px;font-style:italic';
      empty.textContent = '(no images yet)';
      list.appendChild(empty);
    } else {
      images.forEach((ref, i) => {
        const res = this.ctx.getResource(ref);
        const item = document.createElement('div');
        item.style.cssText =
          'display:flex;align-items:center;gap:6px;background:#10141a;border:1px solid #1c222a;border-radius:3px;padding:3px 6px';
        const idx = document.createElement('span');
        idx.style.cssText = 'color:#7d8a96;font-size:11px;min-width:18px';
        idx.textContent = `${i + 1}.`;
        const name = document.createElement('span');
        name.style.cssText = 'flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px';
        name.textContent = res?.name ?? `(missing: ${ref.$ref})`;
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.textContent = '✕';
        rm.title = 'Remove this image';
        rm.style.cssText =
          'background:transparent;color:#c4737a;border:1px solid #3a2030;border-radius:3px;padding:1px 6px;font-size:11px;cursor:pointer';
        rm.addEventListener('click', () => {
          const nextImages = images.filter((_, j) => j !== i);
          onChange({
            images: nextImages,
            autoAdvanceSeconds: this.config.autoAdvanceSeconds,
          } satisfies ImageWidgetConfig);
        });
        item.appendChild(idx);
        item.appendChild(name);
        item.appendChild(rm);
        list.appendChild(item);
      });
    }
    form.appendChild(list);
    form.appendChild(status);

    host.appendChild(form);
  }

  getState(): ImageWidgetState {
    return { currentIndex: this.currentIndex };
  }

  applyState(s: unknown): void {
    if (!s || typeof s !== 'object') return;
    const next = s as ImageWidgetState;
    if (typeof next.currentIndex === 'number' && Number.isFinite(next.currentIndex)) {
      this.currentIndex = next.currentIndex;
      this.renderCurrentImage();
    }
  }

  dispose(): void {
    this.stopAutoAdvance();
    this.host.innerHTML = '';
  }
}

function normaliseConfig(cfg: unknown): ImageWidgetConfig {
  if (cfg && typeof cfg === 'object') {
    const c = cfg as Partial<ImageWidgetConfig>;
    const imgs = Array.isArray(c.images) ? c.images.filter(isResourceRef) : [];
    const secs =
      typeof c.autoAdvanceSeconds === 'number' && Number.isFinite(c.autoAdvanceSeconds) && c.autoAdvanceSeconds > 0
        ? c.autoAdvanceSeconds
        : undefined;
    return { images: imgs, autoAdvanceSeconds: secs };
  }
  return { images: [] };
}

export const imageWidget: DashboardWidget = {
  id: 'image',
  label: 'Image / PDF',
  description: 'Upload one or more images or PDFs; paged viewer. Stored as refs into layout resources.',
  mount(host, config, ctx) {
    return new ImageWidgetInstance(host, normaliseConfig(config), ctx);
  },
};
